import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageLoader } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { AdminLayout, btnPrimary, btnSecondary, ConfirmDelete, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";
import { StatusToggle } from "@/pages/admin/ProjectsPage";
import { Uploader } from "@/components/admin/Uploader";
import { MediaItem } from "@/components/admin/MediaItem";

const Details = ({ project, onSaved }) => {
  const [f, setF] = useState({ title: project.title, description: project.description, status: project.status });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => {
    setSaved(false);
    setF({ ...f, [k]: v });
  };
  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      onSaved((await api.put(`/admin/projects/${project.id}`, f)).data);
      setSaved(true);
      toast.success("Saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Project details" testId="project-details-panel">
      <form onSubmit={save} noValidate className="space-y-4">
        <div>
          <Label htmlFor="pe-title">Title / Make &amp; Model <span className="text-zinc-500">(optional)</span></Label>
          <Input id="pe-title" placeholder="e.g. 1969 Camaro — leave blank to just show photos" value={f.title} onChange={(e) => set("title", e.target.value)} className={`${inputCls} mt-1.5`} data-testid="project-title-input" />
          <FieldError msg={error} testId="project-title-error" />
        </div>
        <StatusToggle value={f.status} onChange={(v) => set("status", v)} prefix="project" />
        <Textarea placeholder="Description" rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className="bg-[#121214] border-zinc-700 text-base" data-testid="project-description-input" />
        <div className="flex items-center gap-4">
          <Button type="submit" disabled={busy} className={btnPrimary} data-testid="project-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </Button>
          {saved && <span className="inline-flex items-center gap-1.5 text-[#7ee08a]" data-testid="project-saved-message"><CheckCircle2 className="h-5 w-5" /> Saved</span>}
        </div>
      </form>
    </Panel>
  );
};

export default function ProjectEditPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [params] = useSearchParams();
  const quickUpload = params.get("upload") === "1";

  const load = useCallback(() => api.get(`/admin/projects/${projectId}`).then((r) => setProject(r.data)), [projectId]);

  useEffect(() => {
    load().catch(() => navigate("/admin/projects", { replace: true }));
  }, [load, navigate]);

  const processing = project?.media.some((m) => m.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [processing, load]);

  if (!project) return <PageLoader testId="project-edit-loading" />;

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggleVisible = (visible) =>
    act(async () => setProject((await api.put(`/admin/projects/${project.id}`, { visible })).data), visible ? "Now showing on the website" : "Hidden from the website");
  const setCover = (media_id) => act(async () => setProject((await api.post(`/admin/projects/${project.id}/cover`, { media_id })).data), "Cover updated");
  const delProject = () => act(async () => {
    await api.delete(`/admin/projects/${project.id}`);
    navigate("/admin/projects", { replace: true });
  }, "Project deleted");
  const patchMedia = (m) => setProject((p) => ({ ...p, media: p.media.map((x) => (x.id === m.id ? m : x)) }));
  const dropMedia = () => load();

  return (
    <AdminLayout title={project.title || "Untitled project"} back="/admin/projects" backLabel="All projects" testId="admin-project-edit-page">
      {quickUpload && <Uploader projectId={project.id} onUploaded={() => load()} autoOpen />}
      <Panel testId="project-visibility-panel">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-base font-medium text-zinc-100">Show on website</p>
            <p className="text-sm text-zinc-400">{project.visible ? "Visitors can see this project." : "Hidden — only you can see it."}</p>
          </div>
          <Switch checked={project.visible} onCheckedChange={toggleVisible} className="scale-125" data-testid="project-visible-switch" />
        </div>
        {project.visible && (
          <a href={`/work/${project.id}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex h-11 items-center gap-1.5 text-sm text-[#FFD23F]" data-testid="project-view-public-link">
            <ExternalLink className="h-4 w-4" /> View on website
          </a>
        )}
      </Panel>
      <Details key={project.id} project={project} onSaved={setProject} />
      {!quickUpload && <Uploader projectId={project.id} onUploaded={() => load()} />}
      <Panel title={`Photos & videos (${project.media.length})`} testId="project-media-panel">
        {!project.media.some((m) => m.status === "ready") && (
          <p className="mb-4 rounded-md border border-[#FFD23F]/40 bg-[#FFD23F]/10 p-3 text-sm text-[#FFD23F]" data-testid="project-no-media-hint">Add photos or videos — this project is hidden from your site until it has at least one</p>
        )}
        {project.media.length === 0 ? (
          <p className="text-zinc-400" data-testid="project-media-empty">Nothing yet — add your first photos above.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="admin-media-list">
            {project.media.map((m) => (
              <MediaItem key={m.id} m={m} isCover={project.cover_media_id === m.id} onChanged={patchMedia} onDeleted={dropMedia} onCover={setCover} />
            ))}
          </ul>
        )}
      </Panel>
      <Panel testId="project-danger-panel">
        <ConfirmDelete
          description="This project and all of its photos and videos will be deleted for good."
          confirmLabel="Yes, delete project"
          onConfirm={delProject}
          trigger={<Button className={`${btnSecondary} w-full text-[#ff8a80]`} data-testid="project-delete-button"><Trash2 className="h-4 w-4" /> Delete this project</Button>}
        />
      </Panel>
    </AdminLayout>
  );
}
